import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Flex,
  Text,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Badge,
  VStack,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
} from "@chakra-ui/react";
import CTable from "../../../components/Table/table";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { getHomeworkReportByChapterRequest } from "../../../modules/admin-modules/homework/Actions";
import { getAllChaptersRequest } from "../../../modules/admin-modules/chapters/Actions";
import { getAssistantsRequest } from "../../../modules/admin-modules/teachersAssistant/Actions";
import Select from "react-select";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

function sanitizeExcelSheetName(name) {
  let s = String(name);
  for (const c of ["\\", "/", "*", "?", ":", "[", "]"]) {
    s = s.split(c).join(" ");
  }
  s = s.trim().replace(/\s+/g, " ");
  return (s || "Sheet").slice(0, 31);
}

/**
 * HomeworkReportByChapter Component
 * 
 * This component displays homework reports organized by chapter and homework.
 * Features:
 * - Chapter selection dropdown
 * - Tabbed interface showing different homeworks for the selected chapter
 * - Table displaying student homework records with evaluation and assistant data
 * - Assistant statistics showing how many homeworks each assistant has checked
 * - CSV export functionality
 * - Responsive design with loading states
 */
const HomeworkReportByChapter = () => {
  const dispatch = useDispatch();
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const setPage = (page) => {
    // This function is required by CTable but not used in this implementation
  };

  const homeworkReportData = useSelector(
    (state) => state.homework.homeworkReportByChapter
  );
  const isLoading = useSelector((state) => state.homework.isLoading);
  const chapters = useSelector((state) => state.chapters.chapters);
  const assistants = useSelector((state) => state.teacherassistants.assistants);

  useEffect(() => {
    dispatch(getAllChaptersRequest());
    dispatch(getAssistantsRequest());
  }, []);

  useEffect(() => {
    if (selectedChapter) {
      dispatch(getHomeworkReportByChapterRequest(selectedChapter.value));
    }
  }, [selectedChapter]);

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      width: "100%",
      background: "#f5f5f5",
      color: "#690000",
      borderRadius: "16px",
    }),
    option: (styles) => ({ ...styles, color: "#690000" }),
  };

  const chapterOptions = chapters?.map((chapter) => ({
    value: chapter.id,
    label: chapter.name,
  }));

  // Get homework names from the data
  const homeworkNames = homeworkReportData?.data && typeof homeworkReportData.data === 'object' ? Object.keys(homeworkReportData.data) : [];

  // Calculate assistant statistics
  const getAssistantStats = () => {
    if (!homeworkReportData?.data) return { assistantStats: {}, totalChecked: 0 };

    const assistantStats = {};
    let totalChecked = 0;

    try {
      Object.values(homeworkReportData.data).forEach((homeworkRecords) => {
        if (Array.isArray(homeworkRecords)) {
          homeworkRecords.forEach((record) => {
            if (record.assistant) {
              const assistantName = record.assistant.name || "Unknown";
              if (!assistantStats[assistantName]) {
                assistantStats[assistantName] = 0;
              }
              assistantStats[assistantName]++;
              totalChecked++;
            }
          });
        }
      });
    } catch (error) {
      console.error("Error processing assistant stats:", error);
    }

    return { assistantStats, totalChecked };
  };

  const { assistantStats, totalChecked } = getAssistantStats();

  // Prepare data for current tab
  const getCurrentTabData = () => {
    if (!homeworkReportData?.data || homeworkNames.length === 0) return [];

    const currentHomeworkName = homeworkNames[activeTab];
    const homeworkRecords = homeworkReportData.data[currentHomeworkName] || [];

    try {
      return homeworkRecords.map((record, index) => ({
        ...record,
        index: `#${index + 1}`,
        student_name: record.student?.name || "N/A",
        student_username: record.student?.user_name || "N/A",
        evaluation: record.evaluation || "No evaluation",
        assistant_comment: record.assistant_comment || "No comment",
        assistant_name: record.assistant?.name || "No assistant",
        status: record.status === 1 ? "Submitted" : "Not Submitted",
        is_solved: record.is_solved === 1 ? "Solved" : "Not Solved",
        status_badge: (
          <Badge
            colorScheme={record.status === 1 ? "green" : "red"}
            variant="subtle"
          >
            {record.status === 1 ? "Submitted" : "Not Submitted"}
          </Badge>
        ),
        solved_badge: (
          <Badge
            colorScheme={record.is_solved === 1 ? "green" : "red"}
            variant="subtle"
          >
            {record.is_solved === 1 ? "Solved" : "Not Solved"}
          </Badge>
        ),
      }));
    } catch (error) {
      console.error("Error processing current tab data:", error);
      return [];
    }
  };

  const currentTabData = getCurrentTabData();

  // Reset active tab if it's out of bounds
  useEffect(() => {
    if (homeworkNames.length > 0 && activeTab >= homeworkNames.length) {
      setActiveTab(0);
    }
  }, [homeworkNames.length, activeTab]);

  const columns = [
    {
      Header: "رقم الطالب",
      accessor: "index",
    },
    {
      Header: "اسم الطالب",
      accessor: "student_name",
    },
    {
      Header: "اسم المستخدم",
      accessor: "student_username",
    },
    {
      Header: "التقييم",
      accessor: "evaluation",
    },
    {
      Header: "تعليق المساعد",
      accessor: "assistant_comment",
    },
    {
      Header: "اسم المساعد",
      accessor: "assistant_name",
    },
    {
      Header: "الحالة",
      accessor: "status_badge",
    },
    {
      Header: "تم الحل",
      accessor: "solved_badge",
    },
  ];

  const viewData = (data) => {
    console.log("Selected data:", data);
  };

  // Export to Excel with multiple sheets (ExcelJS — avoids vulnerable `xlsx` / SheetJS CE)
  const exportToExcel = async () => {
    if (!homeworkReportData?.data) {
      return;
    }

    const workbook = new ExcelJS.Workbook();

    Object.entries(homeworkReportData.data).forEach(([homeworkName, records]) => {
      if (!Array.isArray(records) || records.length === 0) {
        return;
      }

      const sheet = workbook.addWorksheet(sanitizeExcelSheetName(homeworkName));

      sheet.columns = [
        { header: "Student Name", key: "studentName", width: 25 },
        { header: "Student Username", key: "studentUsername", width: 15 },
        { header: "Evaluation", key: "evaluation", width: 50 },
        { header: "Assistant Comment", key: "assistantComment", width: 30 },
        { header: "Assistant Name", key: "assistantName", width: 25 },
      ];

      records.forEach((record) => {
        sheet.addRow({
          studentName: record.student?.name || "N/A",
          studentUsername: record.student?.user_name || "N/A",
          evaluation: record.evaluation || "No evaluation",
          assistantComment: record.assistant_comment || "No comment",
          assistantName: record.assistant?.name || "No assistant",
        });
      });

      const assistantStats = {};
      records.forEach((record) => {
        if (record.assistant?.name) {
          const assistantName = record.assistant.name;
          assistantStats[assistantName] = (assistantStats[assistantName] || 0) + 1;
        }
      });

      if (Object.keys(assistantStats).length > 0) {
        sheet.addRow({});
        sheet.addRow({
          studentName: "Assistant Statistics:",
          studentUsername: "",
        });
        Object.entries(assistantStats).forEach(([assistantName, count]) => {
          sheet.addRow({ studentName: assistantName, studentUsername: count });
        });
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const fileName = `homework-report-${
      selectedChapter?.label || "chapter"
    }-${new Date().toISOString().split("T")[0]}.xlsx`;
    saveAs(blob, fileName);
  };

  return (
    <Layout>
      <div style={{ minHeight: "400px" }}>
        <Flex
          w="100%"
          my="30px"
          justifyContent="space-between"
          alignItems="center"
          position="relative"
          zIndex="10"
          flexWrap="wrap"
        >
          <Text fontSize="24px" fontWeight="600" color="main_1">
            تقرير الواجبات حسب الفصل
          </Text>
          <Flex
            w={{ base: "100%", md: "70%" }}
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Box w="50%">
              <Select
                options={chapterOptions}
                isMulti={false}
                onChange={setSelectedChapter}
                styles={colourStyles}
                placeholder="اختر الفصل"
              />
            </Box>
            {homeworkNames.length > 0 && (
              <Box
                mx="10px"
                border="1px solid gray"
                borderRadius="8px"
                padding="10px"
              >
                <Button
                  onClick={exportToExcel}
                  colorScheme="green"
                  size="sm"
                  leftIcon={<span>📊</span>}
                >
                  Export to Excel
                </Button>
              </Box>
            )}
            <LogoutMenu />
          </Flex>
        </Flex>

        <Box mb={6} p={4} bg="gray.50" borderRadius="lg">
          <Text fontSize="18px" fontWeight="600" mb={4} color="main_1">
            إحصائيات عامة
          </Text>
          <StatGroup>
            {assistants?.data?.map((assistant) => (
              <Stat key={assistant?.id}>
                <StatLabel>{assistant?.name}</StatLabel>
                <StatNumber>{assistant?.homework_count || 0}</StatNumber>
              </Stat>
            ))}
          </StatGroup>
        </Box>

        {/* Assistant Statistics */}
        {assistantStats && Object.keys(assistantStats).length > 0 && (
          <Box mb={6} p={4} bg="gray.50" borderRadius="lg">
            <Text fontSize="18px" fontWeight="600" mb={4} color="main_1">
              إحصائيات المساعدين
            </Text>
            <StatGroup>
              {Object.entries(assistantStats).map(([assistantName, count]) => (
                <Stat key={assistantName}>
                  <StatLabel>{assistantName}</StatLabel>
                  <StatNumber>{count}</StatNumber>
                </Stat>
              ))}
              <Stat>
                <StatLabel>إجمالي الواجبات المفحوصة</StatLabel>
                <StatNumber>{totalChecked}</StatNumber>
              </Stat>
            </StatGroup>
          </Box>
        )}

        {/* Tabs */}
        {homeworkNames.length > 0 && (
          <Box mb={6}>
            <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed">
              <TabList>
                {homeworkNames.map((homeworkName, index) => (
                  <Tab key={index} fontSize="sm">
                    {homeworkName}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                {homeworkNames.map((homeworkName, index) => (
                  <TabPanel key={index}>
                    <CTable
                      selectedData={viewData}
                      Columns={columns}
                      Data={getCurrentTabData()}
                      Actions
                      currentpage={pageNumber}
                      setPage={setPage}
                      setPageNumber={setPageNumber}
                      totalPage={Math.ceil(currentTabData.length / 10)}
                      isLoading={isLoading}
                    />
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </Box>
        )}

        {/* No data message */}
        {(!homeworkReportData?.data || (homeworkReportData?.data && Object.keys(homeworkReportData.data).length === 0)) && !isLoading && (
          <Box textAlign="center" py={10}>
            <Text fontSize="18px" color="gray.500">
              {selectedChapter
                ? "لا توجد بيانات للفصل المحدد"
                : "يرجى اختيار فصل لعرض البيانات"}
            </Text>
          </Box>
        )}

        {/* Loading message */}
        {isLoading && (
          <Box textAlign="center" py={10}>
            <Text fontSize="18px" color="gray.500">
              جاري تحميل البيانات...
            </Text>
          </Box>
        )}
      </div>
    </Layout>
  );
};

export default HomeworkReportByChapter; 